import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Divider,
  Typography,
  CircularProgress
} from '@mui/material';
import { Save, Trash } from 'lucide-react';
import { FilterFieldSx, CSS_PRIMARY } from '../../../../constants/style';
import { saveCity, deleteCity } from '../../../../apis/Masters/City';
import { saveDcrCity, deleteDcrCity } from '../../../../apis/DCR/City';
import ConfirmDialog from '../../common/ConfirmDialog';

/* ── Empty form state ─────────────────────────────────────────────────────── */
const emptyForm = {
  cityName: '',
  isActive: true,
  cityGUID: 0
};

/* ── Validation ───────────────────────────────────────────────────────────── */
const validate = (form) => {
  const errors = {};
  if (!form.cityName.trim()) {
    errors.cityName = 'City Name is required.';
  }
  return errors;
};

const AddCity = ({ isDcrCity = false, cityData = null, userAccess, onRefresh, onClose }) => {
  const isEditMode = Boolean(cityData);

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isDeleteAllowd = isEditMode ? userAccess?.hasDeletePermission : false;
  const isEditAllowed = userAccess?.hasEditPermission || userAccess?.hasAddPermission;

  // ── Confirm-dialog state ────────────────────────────────────────────────
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    content: '',
    onConfirm: null
  });

  const openConfirm = ({ title, content, onConfirm }) =>
    setConfirmDialog({ open: true, title, content, onConfirm });

  const closeConfirm = () =>
    setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));

  const deleteHandler = () => {
    if (!userAccess?.hasDeletePermission) return;
    openConfirm({
      title: 'Delete City Record',
      content: `Are you sure you want to delete the City record for "${cityData.city || 'this city'
        }"? This action cannot be undone.`,
      onConfirm: () => {
        closeConfirm();
        handleDelete();
      }
    });
  };

  useEffect(() => {
    if (cityData) {
      setForm({
        cityName: cityData.city || cityData.dailyCallCity || '',
        isActive: cityData.isActive,
        cityGUID: cityData.cityGUID || cityData.dailyCallCityGUID || 0
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
    setTouched({});
  }, [cityData]);

  // ── Field change ───────────────────────────────────────────────────────────
  const handleChange = (field) => (e) => {
    const value =
      e.target.type === 'checkbox' ? e.target.checked : (e.target.value || '');
    const next = { ...form, [field]: value };
    setForm(next);

    // Live-validate touched fields
    if (touched[field]) {
      const errs = validate(next);
      setErrors((prev) => ({ ...prev, [field]: errs[field] }));
    }
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const errs = validate(form);
    setErrors((prev) => ({ ...prev, [field]: errs[field] }));
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    // Touch all fields to reveal errors
    const allTouched = Object.fromEntries(Object.keys(form).map((k) => [k, true]));
    setTouched(allTouched);

    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    try {
      const payload = {
        isActive: form.isActive,
        city: (form.cityName.trim()).toUpperCase(),
        cityGUID: form.cityGUID,
        sortOrder: 0
      };
      const dcrPaylod = {
        dailyCallCity: form.cityName.trim(),
        dailyCallCityGUID: form.cityGUID,
        isActive: form.isActive,
        sortOrder: 0
      };
      const apiPayload = isDcrCity ? dcrPaylod : payload;
      const response = isDcrCity ? await saveDcrCity(apiPayload) : await saveCity(apiPayload);
      if (response?.success) {
        onClose?.();
        onRefresh?.();
      }
    } catch (err) {
      console.log('Failed to save city:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const successMsg = `City deleted successfully`;
      const cityGUID = isDcrCity ? cityData?.dailyCallCityGUID : cityData?.cityGUID;
      const response = isDcrCity
        ? await deleteDcrCity(cityGUID, successMsg, '')
        : await deleteCity(cityGUID, successMsg, '');
      if (response && response.success) {
        onClose?.();
        onRefresh?.();
      }
    } catch (err) {
      console.log('Failed to delete global type:', err);
    } finally {
      setDeleting(false);
    }
  };

  // ── Has any visible error ──────────────────────────────────────────────────
  const hasErrors = Object.keys(validate(form)).length > 0;

  return (
    <>
      <Box component="form" noValidate autoComplete="off">
        <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 2.5 }}>
          {/* Name */}
          <Box sx={{ flex: '1 1 50%', minWidth: '200px' }}>
            <TextField
              fullWidth
              label="City"
              placeholder="e.g. New York"
              value={form.cityName}
              onChange={handleChange('cityName')}
              onBlur={handleBlur('cityName')}
              error={touched.cityName && Boolean(errors.cityName)}
              helperText={touched.cityName && errors.cityName}
              size="small"
              disabled={!isEditAllowed}
              sx={{
                ...FilterFieldSx,
                '& input': {
                  textTransform: 'uppercase'
                }
              }}
            />
          </Box>

          {/* isActive */}
          <Box sx={{ flex: '1 1 50%', minWidth: '200px', display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.isActive}
                  onChange={handleChange('isActive')}
                  disabled={!isEditAllowed}
                  size="small"
                  sx={{
                    color: 'primary.main',
                    '&.Mui-checked': { color: CSS_PRIMARY }
                  }}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Active
                </Typography>
              }
            />
          </Box>
        </Box>

        {/* ── Actions ─────────────────────────────────────────────────────── */}

        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={
              deleting ? <CircularProgress size={14} color="inherit" /> : <Trash size={15} />
            }
            disabled={deleting}
            onClick={deleteHandler}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 100,
              background: '#e05757',
              '&:hover': { background: '#6d6464' },
              boxShadow: '0 4px 12px rgba(46,204,113,0.3)',
              display: !isEditMode || !isDeleteAllowd ? 'none' : 'inline-flex'
            }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>

          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save size={15} />}
            disabled={saving || (hasErrors && Object.keys(touched).length > 0)}
            onClick={handleSave}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 100,
              background: 'var(--primary, #36c95f)',
              '&:hover': { background: 'var(--primary-dark, #27ae60)' },
              boxShadow: '0 4px 12px rgba(46,204,113,0.3)'
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Box>

      {/* ── Confirm Dialog ─────────────────────────────────────────────── */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={closeConfirm}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        content={confirmDialog.content}
        titleIcon={<Trash size={18} />}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />
    </>
  );
};

export default AddCity;
